import './orgChart.css';

/**
 * Human labels for each warning code, matching docs/ADMIN_GUIDE.md so the
 * admin-facing wording and the documented reference stay in sync.
 */
const CODE_LABELS = {
  no_parent_job: 'No Report To Job configured',
  employee_without_job: 'Employee has no Job Position set',
  employee_job_missing: 'Employee Job Position not in the last synced data',
  unresolvable_parent: 'Report To Job points at an unknown job',
  out_of_scope_parent: 'Parent job is outside the configured companies',
  node_without_company: 'No company set in Odoo',
  manager_mismatch: 'Odoo manager disagrees with the reporting line',
  cycle_broken: 'Reporting loop detected and cut',
  no_nexus_account: 'No matching Nexus account',
  invalid_company_setting: 'Company display setting is empty or invalid',
  unknown_status_key: 'Employment status value has no label yet',
};

// Real data can carry hundreds of entries for one code (manager_mismatch was
// 288 on a live sync); rendering all of them as list items makes the panel
// unusable, so each group is capped and the remainder is summarised.
const MAX_MESSAGES_PER_GROUP = 20;

// manager_mismatch is the largest group on real data (over 200 entries) and
// each one is a three-way disagreement (person, Odoo manager, reporting
// line), which reads far better as a table than as one prose sentence per
// row. Every other code keeps the plain message-list rendering below: this
// is a targeted table for the one code that benefits from it, not a general
// warning-table renderer.
const MANAGER_MISMATCH_CODE = 'manager_mismatch';

function labelFor(code) {
  return CODE_LABELS[code] || code;
}

/**
 * manager_mismatch as a table: person, Odoo manager, reporting line. buildNodes
 * (server/services/orgChart/buildNodes.js) puts those three values on the
 * warning as data (`person`/`odooManager`/`reportingLineManager`), which is
 * what makes a table possible instead of a parsed sentence. `message` is kept
 * as a fallback for a row that predates those fields (or any other producer
 * that only ever set `message`), rendered as a single wide cell so the row
 * still shows something rather than three blanks.
 */
function ManagerMismatchTable({ rows }) {
  return (
    <div className="oc-dq-table-wrap">
      <table className="oc-dq-table">
        <thead>
          <tr>
            <th scope="col">Person</th>
            <th scope="col">Odoo manager</th>
            <th scope="col">Reporting line</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((w, i) => {
            const hasStructuredFields = w.person != null || w.odooManager != null || w.reportingLineManager != null;
            return (
              <tr key={`mismatch-${i}`}>
                {hasStructuredFields ? (
                  <>
                    <td>{w.person ?? 'n/a'}</td>
                    <td>{w.odooManager ?? 'n/a'}</td>
                    <td>{w.reportingLineManager ?? 'n/a'}</td>
                  </>
                ) : (
                  <td colSpan={3}>{w.message}</td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Groups warnings by code, admin org chart tab only. Warnings name individual
 * people (an employee with no job, a manager mismatch), so managers never
 * receive them from the server in the first place; this component only ever
 * renders for an admin caller.
 *
 * Groups render expanded by default, since the admin view exists specifically
 * to let an admin scan every issue rather than hunt through a collapsed list.
 *
 * `outOfScope` is the server's per-code tally of warnings that named a node
 * the company filter hid (`warnings_out_of_scope` on the org chart payload).
 * The chart is built over every company so cross-company reporting lines
 * still resolve, so a code can have issues outside the companies currently
 * shown; those are summarised as a count rather than listed, since the admin
 * is not looking at those companies right now. A code that has ONLY
 * out-of-scope entries still gets its own group, with just that summary line,
 * so the admin is not blind to it.
 */
export default function OrgDataQualityPanel({ warnings, outOfScope }) {
  const inScope = warnings || [];
  const outOfScopeCounts = outOfScope || {};
  const hasOutOfScope = Object.keys(outOfScopeCounts).length > 0;
  if (inScope.length === 0 && !hasOutOfScope) return null;

  const groups = new Map();
  for (const w of inScope) {
    if (!groups.has(w.code)) groups.set(w.code, []);
    groups.get(w.code).push(w);
  }
  // Codes present only out of scope still need a group, so the admin sees a
  // header for them even though there is no in-scope message to list.
  for (const code of Object.keys(outOfScopeCounts)) {
    if (!groups.has(code)) groups.set(code, []);
  }
  const sortedCodes = [...groups.keys()].sort((a, b) => {
    const totalA = groups.get(a).length + (outOfScopeCounts[a] || 0);
    const totalB = groups.get(b).length + (outOfScopeCounts[b] || 0);
    return totalB - totalA;
  });

  const total = inScope.length;
  const label = total === 1 ? '1 data issue' : `${total} data issues`;

  return (
    <section className="oc-dq" aria-label="Org chart data quality">
      <h3 className="oc-dq-title">{label}</h3>
      <div className="oc-dq-groups">
        {sortedCodes.map((code) => {
          const items = groups.get(code);
          const shown = items.slice(0, MAX_MESSAGES_PER_GROUP);
          const remaining = items.length - shown.length;
          const outCount = outOfScopeCounts[code] || 0;
          return (
            <details className="oc-dq-group" key={code} open>
              <summary>
                <strong>{labelFor(code)}</strong>
                <span className="oc-dq-code">{code}</span>
                <span className="oc-dq-count">{items.length}</span>
              </summary>
              {items.length > 0 && code === MANAGER_MISMATCH_CODE && (
                <ManagerMismatchTable rows={shown} />
              )}
              {items.length > 0 && code !== MANAGER_MISMATCH_CODE && (
                <ul className="oc-dq-list">
                  {shown.map((w, i) => (
                    <li key={`${code}-${i}`}>{w.message}</li>
                  ))}
                </ul>
              )}
              {remaining > 0 && (
                <p className="oc-dq-more">and {remaining} more</p>
              )}
              {outCount > 0 && (
                <p className="oc-dq-more">
                  and {outCount} more in companies not currently shown
                </p>
              )}
            </details>
          );
        })}
      </div>
    </section>
  );
}
